(() => {
  const formatMoney = (currency, cents) => {
    try {
      return new Intl.NumberFormat("fr-FR", { style: "currency", currency: currency || "EUR" }).format((cents || 0) / 100);
    } catch (_) {
      return `${((cents || 0) / 100).toFixed(2)} EUR`;
    }
  };

  const reveal = (root) => {
    const reveals = root.querySelectorAll(".kt-kit-reveal");
    if (!reveals.length || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.18 });
    reveals.forEach((item) => observer.observe(item));
  };

  const getMainTargets = () => {
    const mainRoot = document.getElementById("MainProduct-main");
    const qty = mainRoot?.querySelector('input[name="quantity"]');
    const variantButton = mainRoot?.querySelector(".quantity-break, button, select, input[type='radio'], [role='button']");
    const quantityBreaks = mainRoot ? [...mainRoot.querySelectorAll(".quantity-break[data-quantity]")] : [];
    return { mainRoot, qty, variantButton, quantityBreaks };
  };

  const getFallbackTargets = (root) => {
    const productRoot = root?.querySelector("[data-kt-product-root]");
    const qty = productRoot?.querySelector("[data-kt-qty-input]");
    const variantButton = productRoot?.querySelector("[data-kt-option-btn], [data-kt-submit]");
    return { productRoot, qty, variantButton };
  };

  const focusMainProduct = (root) => {
    const { mainRoot, qty, variantButton } = getMainTargets();
    const { productRoot, qty: fallbackQty, variantButton: fallbackVariantButton } = getFallbackTargets(root);
    const targetRoot = mainRoot || productRoot;
    const targetFocus = variantButton || qty || fallbackVariantButton || fallbackQty;
    if (!targetRoot) return;
    targetRoot.scrollIntoView({ behavior: "smooth", block: "start" });
    targetRoot.classList.remove("kt-kit-highlight-main");
    window.setTimeout(() => targetRoot.classList.add("kt-kit-highlight-main"), 120);
    window.setTimeout(() => {
      targetFocus?.focus?.();
    }, 420);
  };

  const bubbleField = (field) => {
    if (!field) return;
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const getQuantityBreakByQty = (packQty) => {
    const { quantityBreaks } = getMainTargets();
    if (!packQty) return null;
    return quantityBreaks.find((item) => parseInt(item.getAttribute("data-quantity") || "0", 10) === packQty) || null;
  };

  const syncMainQuantityBreak = (packQty) => {
    const { qty } = getMainTargets();
    const matchedBreak = getQuantityBreakByQty(packQty);
    if (matchedBreak) {
      const inputId = matchedBreak.getAttribute("for");
      const input = inputId ? document.getElementById(inputId) : null;
      if (input) {
        input.checked = true;
        bubbleField(input);
      }
      matchedBreak.click?.();
    }
    if (qty && packQty > 0) {
      qty.value = `${packQty}`;
      bubbleField(qty);
    }
    return matchedBreak;
  };

  const initConfigurator = (root) => {
    const productRoot = root.querySelector("[data-kt-product-root]");
    if (!productRoot) return;
    const jsonEl = productRoot.querySelector("[data-kt-product-json]");
    if (!jsonEl) return;

    let payload;
    try {
      payload = JSON.parse(jsonEl.textContent);
    } catch (_) {
      return;
    }

    const variantInput = productRoot.querySelector("[data-kt-variant-id]");
    const optionGroups = [...productRoot.querySelectorAll("[data-kt-option-group]")];
    const priceEl = productRoot.querySelector("[data-kt-price]");
    const compareEl = productRoot.querySelector("[data-kt-compare]");
    const stockEl = productRoot.querySelector("[data-kt-stock]");
    const submitEl = productRoot.querySelector("[data-kt-submit]");
    const qtyInput = productRoot.querySelector("[data-kt-qty-input]");
    const packStatus = root.querySelector("[data-kt-pack-status]");
    const configureTrigger = root.querySelector("[data-kt-configure-trigger]");
    const autoBundlePrices = root.dataset.ktAutoBundlePrices === "true";
    const sectionDiscounts = {
      2: parseFloat(root.dataset.ktDiscount2 || "0") || 0,
      3: parseFloat(root.dataset.ktDiscount3 || "0") || 0,
      4: parseFloat(root.dataset.ktDiscount4 || "0") || 0
    };
    let currentVariant = null;
    const selections = optionGroups.map((group) => {
      const active = group.querySelector("[aria-pressed='true']");
      return active ? active.getAttribute("data-kt-option-btn") : null;
    });

    const findVariant = () => payload.variants.find((variant) => variant.options.every((opt, idx) => opt === selections[idx]));

    const updateButtons = () => {
      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll("[data-kt-option-btn]").forEach((button) => {
          const value = button.getAttribute("data-kt-option-btn");
          button.setAttribute("aria-pressed", selections[groupIndex] === value ? "true" : "false");
        });
      });
    };

    const updateVariant = () => {
      const variant = findVariant() || payload.variants[0];
      if (!variant) return;
      currentVariant = variant;
      if (variantInput) variantInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(payload.currency, variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.hidden = false;
          compareEl.textContent = formatMoney(payload.currency, variant.compare_at_price);
        } else {
          compareEl.hidden = true;
          compareEl.textContent = "";
        }
      }
      if (stockEl) {
        stockEl.textContent = variant.available ? productRoot.dataset.ktStateAvailable : productRoot.dataset.ktStateUnavailable;
        stockEl.classList.toggle("is-available", !!variant.available);
        stockEl.classList.toggle("is-unavailable", !variant.available);
      }
      if (submitEl) submitEl.disabled = !variant.available;
    };

    const applyVariantOptions = (variant) => {
      if (!variant || !Array.isArray(variant.options)) return false;
      variant.options.forEach((value, index) => {
        if (typeof value === "string") selections[index] = value;
      });
      updateButtons();
      updateVariant();
      return true;
    };

    const selectPackVariant = (card) => {
      const optionValue = (card.getAttribute("data-kt-pack-option") || "").trim().toLowerCase();
      const variantHint = (card.getAttribute("data-kt-pack-variant-hint") || "").trim().toLowerCase();

      if (optionValue) {
        let matched = false;
        optionGroups.forEach((group, index) => {
          if (matched) return;
          const button = [...group.querySelectorAll("[data-kt-option-btn]")].find((item) => {
            return (item.getAttribute("data-kt-option-btn") || "").trim().toLowerCase() === optionValue;
          });
          if (button) {
            selections[index] = button.getAttribute("data-kt-option-btn");
            matched = true;
          }
        });
        if (matched) {
          updateButtons();
          updateVariant();
          return true;
        }
      }

      if (variantHint) {
        const hinted = payload.variants.find((variant) => {
          return (variant.options || []).some((value) => String(value).toLowerCase().includes(variantHint));
        });
        if (hinted) return applyVariantOptions(hinted);
      }

      return false;
    };

    optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll("[data-kt-option-btn]").forEach((button) => {
        button.addEventListener("click", () => {
          selections[groupIndex] = button.getAttribute("data-kt-option-btn");
          updateButtons();
          updateVariant();
        });
      });
    });

    const applyPackSelection = (card, shouldFocus = true) => {
        root.querySelectorAll("[data-kt-pack-card]").forEach((item) => item.classList.remove("is-active"));
        card.classList.add("is-active");
        const explicitStatus = card.getAttribute("data-kt-pack-status") || "";
        if (packStatus) packStatus.textContent = explicitStatus;
        selectPackVariant(card);

        const inferredQty = (card.querySelector("[data-kt-pack-label]")?.textContent || "").match(/\d+/);
        const packQty = parseInt(card.getAttribute("data-kt-pack-qty") || (inferredQty ? inferredQty[0] : "1"), 10);
        if (qtyInput && packQty > 0) qtyInput.value = `${packQty}`;

        const displayPrice = card.getAttribute("data-kt-pack-price");
        const displayCompare = card.getAttribute("data-kt-pack-compare");
        const discountOverride = parseFloat(card.dataset.ktPackDiscountPercentOverride || "0") || 0;
        const matchedBreak = syncMainQuantityBreak(packQty);
        const breakPrice = matchedBreak?.querySelector(".quantity-break__price")?.textContent?.trim();
        const breakCompare = matchedBreak?.querySelector(".quantity-break__compare-price")?.textContent?.trim();
        const breakCaption = matchedBreak?.querySelector(".quantity-break__caption")?.textContent?.trim();
        const effectiveDiscount = discountOverride || sectionDiscounts[packQty] || 0;
        const computedCompareCents = currentVariant ? currentVariant.price * packQty : 0;
        const computedFinalCents = currentVariant ? Math.round(computedCompareCents * (1 - (effectiveDiscount / 100))) : 0;
        if (priceEl) {
          if (autoBundlePrices && currentVariant && packQty > 0) {
            priceEl.textContent = formatMoney(payload.currency, computedFinalCents);
          } else if (displayPrice) {
            priceEl.textContent = displayPrice;
          } else if (breakPrice) {
            priceEl.textContent = breakPrice;
          }
        }
        if (compareEl) {
          if (autoBundlePrices && currentVariant && packQty > 1 && effectiveDiscount > 0) {
            compareEl.hidden = false;
            compareEl.textContent = formatMoney(payload.currency, computedCompareCents);
          } else if (displayCompare) {
            compareEl.hidden = false;
            compareEl.textContent = displayCompare;
          } else if (breakCompare) {
            compareEl.hidden = false;
            compareEl.textContent = breakCompare;
          } else {
            compareEl.hidden = true;
            compareEl.textContent = "";
          }
        }
        if (autoBundlePrices && currentVariant && packStatus && !explicitStatus) {
          if (packQty > 1 && effectiveDiscount > 0) {
            packStatus.textContent = `${packQty} sets actifs - ${effectiveDiscount}% de remise affichée, remise réelle via Shopify au panier`;
          } else {
            packStatus.textContent = `${packQty} set actif - configurez vos tailles à l’étape suivante`;
          }
        } else if (!displayPrice && !displayCompare && breakCaption && packStatus && !explicitStatus) {
          packStatus.textContent = breakCaption;
        }
        if (shouldFocus) focusMainProduct(root);
    };

    root.querySelectorAll("[data-kt-pack-card]").forEach((card) => {
      card.addEventListener("click", () => applyPackSelection(card));
    });

    root.querySelectorAll("[data-kt-qty-minus]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!qtyInput) return;
        qtyInput.value = `${Math.max(1, (parseInt(qtyInput.value || "1", 10) || 1) - 1)}`;
      });
    });

    root.querySelectorAll("[data-kt-qty-plus]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!qtyInput) return;
        qtyInput.value = `${(parseInt(qtyInput.value || "1", 10) || 1) + 1}`;
      });
    });

    configureTrigger?.addEventListener("click", (event) => {
      event.preventDefault();
      if (packStatus) packStatus.textContent = "Configurez tailles et quantité dans la zone produit Shopify";
      focusMainProduct(root);
    });

    updateButtons();
    updateVariant();
    const defaultPack = root.querySelector("[data-kt-pack-card].is-active") || root.querySelector("[data-kt-pack-card]");
    if (defaultPack) applyPackSelection(defaultPack, false);
  };

  const init = (root = document) => {
    reveal(root);
    root.querySelectorAll(".kt-kit-root").forEach((section) => initConfigurator(section));
  };

  document.addEventListener("DOMContentLoaded", () => init(document));
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:select", (event) => init(event.target));
})();
